import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { form, FormField, maxLength, minLength, pattern, required } from '@angular/forms/signals';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzFlexModule } from 'ng-zorro-antd/flex';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTagModule } from 'ng-zorro-antd/tag';

import { NzSpinModule } from 'ng-zorro-antd/spin';
import {
  ServiceProviderType,
  UpdateServiceProviderWebRequest,
} from '../../../../core/api/mhsConfigurationApiClient';
import { NfzBranchLookup } from '../../../../core/context/nhf-branch-lookup';
import { ServiceProviderTypeLookup } from '../../../../core/context/service-provider-type-lookup';
import { FieldErrorsComponent } from '../../../../shared/ui/components/field-error/field-error';
import { ServiceProviderData } from '../../dto/service-provider-data';
import { ServiceProvidersService } from '../../services/service-providers-service';

@Component({
  selector: 'app-service-provider-edit',
  imports: [
    ReactiveFormsModule,
    FormField,
    NzCardModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule,
    NzSpinModule,
    NzCheckboxModule,
    RouterLink,
    NzFlexModule,
    NzGridModule,
    NzDividerModule,
    NzIconModule,
    NzTagModule,
    CommonModule,
    NzAvatarModule,
    NzDropdownModule,
    FieldErrorsComponent,
  ],
  templateUrl: './service-provider-edit.html',
  styleUrl: './service-provider-edit.scss',
})
export class ServiceProviderEditComponent {
  private serviceProviderService = inject(ServiceProvidersService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private messageService = inject(NzMessageService);
  isLoaded = signal(false);
  isSubmitting = signal(false);
  submitted = signal(false);

  serviceProviderTypes = ServiceProviderTypeLookup.types;
  nfzBranchCodes = [{ code: '', name: 'Brak' }, ...NfzBranchLookup.branches];

  serviceProviderModel = signal<ServiceProviderData>({
    name: '',
    type: 'Private',
    regon: '',
    code: '',
    nhfBranchCode: '',
    description: '',
    taxId: '',
    phone: '',
    zip: '',
    place: '',
    street: '',
    registryNumber: '',
  });

  serviceProviderForm = form(this.serviceProviderModel, (serviceProviderModel) => {
    required(serviceProviderModel.name);
    minLength(serviceProviderModel.name, 2, {
      message: 'Nazwa musi mieć przynajmniej 2 znaki',
    });
    maxLength(serviceProviderModel.name, 100, {
      message: 'Nazwa nie może mieć więcej niż 100 znaków',
    });

    required(serviceProviderModel.type);

    required(serviceProviderModel.regon);
    minLength(serviceProviderModel.regon, 9);
    maxLength(serviceProviderModel.regon, 14);
    pattern(serviceProviderModel.regon, /^\d+$/, {
      message: 'REGON musi składać się wyłącznie z cyfr',
    });

    required(serviceProviderModel.taxId);
    minLength(serviceProviderModel.taxId, 10);
    maxLength(serviceProviderModel.taxId, 10);
    pattern(serviceProviderModel.taxId, /^\d+$/, {
      message: 'NIP musi składać się wyłącznie z cyfr',
    });

    required(serviceProviderModel.phone);
    minLength(serviceProviderModel.phone, 9);
    maxLength(serviceProviderModel.phone, 12);
    pattern(serviceProviderModel.phone, /^\d+$/, {
      message: 'Numer telefonu musi składać się wyłącznie z cyfr',
    });

    required(serviceProviderModel.zip);
    pattern(serviceProviderModel.zip, /^\d{2}-\d{3}$/, {
      message: 'Niepoprawny format kodu pocztowego (XX-XXX)',
    });

    required(serviceProviderModel.place);
    required(serviceProviderModel.street);
  });

  constructor() {
    const id = Number(this.route.snapshot.paramMap.get('id'));

    if (id) {
      this.serviceProviderService.get(id).subscribe((provider) => {
        this.serviceProviderModel.set({
          name: provider.name ?? '',
          type: provider.type === ServiceProviderType.Nhf ? 'Nhf' : 'Private',
          regon: provider.regon ?? '',
          code: provider.code ?? '',
          nhfBranchCode: provider.nhfBranchCode ?? '',
          description: provider.description ?? '',
          taxId: provider.taxId ?? '',
          phone: provider.phone ?? '',
          zip: provider.zip ?? '',
          place: provider.place ?? '',
          street: provider.street ?? '',
          registryNumber: provider.registryNumber ?? '',
        });
        this.isLoaded.set(true);
      });
    }
  }

  private mapToApiModel(formValue: ServiceProviderData): UpdateServiceProviderWebRequest {
    const apiModel = new UpdateServiceProviderWebRequest();
    apiModel.id = Number(this.route.snapshot.paramMap.get('id'));
    apiModel.name = formValue.name;
    apiModel.type =
      formValue.type === 'Nhf' ? ServiceProviderType.Nhf : ServiceProviderType.Private;

    apiModel.regon = formValue.regon;
    apiModel.code = formValue.code;
    apiModel.nhfBranchCode = formValue.nhfBranchCode;
    apiModel.description = formValue.description;
    apiModel.taxId = formValue.taxId;
    apiModel.phone = formValue.phone;
    apiModel.zip = formValue.zip;
    apiModel.place = formValue.place;
    apiModel.street = formValue.street;
    apiModel.registryNumber = formValue.registryNumber;

    return apiModel;
  }

  submit() {
    this.submitted.set(true);

    if (!this.serviceProviderForm().valid()) {
      return;
    }

    this.isSubmitting.set(true);

    this.serviceProviderService
      .update(this.mapToApiModel(this.serviceProviderModel()))
      .subscribe({
        next: () => {
          this.messageService.success('Podmiot został zaktualizowany');
          this.router.navigate(['../details'], {
            relativeTo: this.route,
          });
        },
        error: (error) => {
          console.error(error);
        },
      })
      .add(() => this.isSubmitting.set(false));
  }
}
